import { type ItemOf } from '@shared/board'
import { Collaboration } from '@tiptap/extension-collaboration'
import { Placeholder } from '@tiptap/extensions'
import { EditorContent, useEditor } from '@tiptap/react'
import { clsx } from 'clsx'
import { MessageSquare } from 'lucide-react'
import { memo, useEffect, useLayoutEffect, useState } from 'react'
import { useI18n } from '@/i18n/i18n'

type I18nT = ReturnType<typeof useI18n>['t']

import { dbg } from '@/lib/debug'
import { Latest } from '@/lib/latest'
import { cardExtensions, cardField } from '@shared/card-schema'
import 'katex/dist/katex.min.css'
import { DRAG_HANDLE, NO_DRAG, NO_WHEEL } from '../classes'
import { type BoardCtx, useBoardCtx } from '../context'
import { BOARD_LINK_EVENT, BoardLinkClicks, useOpenBoardLink } from '../boardLinks'
import { useBoardDerived } from '../derived'
import { BlockDrops } from '../cardEditor/blockDrag'
import { BlockHandle } from '../cardEditor/BlockHandle'
import { MathEditor } from '../cardEditor/MathEditor'
import { MathStore } from '../cardEditor/mathStore'
import { materialText, mentionAllowed, mentionItems } from '../cardEditor/mentions'
import { SelectionBar } from '../cardEditor/SelectionBar'
import { slashAllowed, slashCommands, slashItems } from '../cardEditor/slash'
import { SuggestStore, suggestExtension } from '../cardEditor/suggest'
import { SuggestList } from '../cardEditor/SuggestList'
import { Author } from './Author'

/**
 * `@` and `/` in a card. Built once per editor; what they read (the board,
 * the materials, the language) is read when they open.
 */
function suggestions(c: {
  client: BoardCtx['client']
  boardId: string
  id: string
  stores: { mention: SuggestStore; slash: SuggestStore; math: MathStore }
  t: Latest<I18nT>
}) {
  const { client, boardId, id, stores } = c
  const tNow = c.t
  return [
    suggestExtension({
      char: '@',
      name: 'mentionSuggest',
      store: stores.mention,
      allowSpaces: true,
      allowAfter: mentionAllowed,
      label: () => tNow.current('mention.label'),
      empty: () => tNow.current('mention.empty'),
      items: (query) =>
        mentionItems(
          {
            view: () => client.view,
            doc: client.doc,
            materials: () => client.getMaterials(),
            text: (m) => materialText(boardId, m),
            self: id,
            t: (k, v) => tNow.current(k, v),
          },
          query,
        ),
    }),
    suggestExtension({
      char: '/',
      name: 'slashSuggest',
      store: stores.slash,
      allowAfter: slashAllowed,
      label: () => tNow.current('slash.label'),
      empty: () => tNow.current('slash.empty'),
      items: (query) =>
        slashItems(
          slashCommands((_, kind) => {
            stores.math.open({ kind, pos: null, latex: '' })
          }),
          (k) => tNow.current(k),
          query,
        ),
    }),
  ]
}

/**
 * The editor lives in its own memoized component keyed only by card id, so the
 * frequent re-renders of the node (size measurements, selection) never reach
 * `useEditor`. Re-rendering it mid-typing re-applies options to the view and
 * can pull the caret back to the start while DOM input is still unread.
 */
const CardEditor = memo(function CardEditor({ id }: { id: string }) {
  const { client, autofocus, initialText, boardId } = useBoardCtx()
  const { t } = useI18n()
  const openLink = useOpenBoardLink()
  // Read by the editor's extensions at the moment they need it: the editor is
  // made once per card and must not be re-made when these change.
  const [tNow] = useState(() => new Latest(t))
  useLayoutEffect(() => {
    tNow.set(t)
  }, [t, tNow])
  const [stores] = useState(() => ({
    mention: new SuggestStore(),
    slash: new SuggestStore(),
    math: new MathStore(),
  }))

  const editor = useEditor(
    {
      extensions: [
        ...cardExtensions({
          onMathClick: (kind, node, pos) => {
            stores.math.open({ kind, pos, latex: String(node.attrs.latex ?? '') })
          },
        }),
        BoardLinkClicks,
        BlockDrops,
        ...suggestions({ client, boardId, id, stores, t: tNow }),
        Collaboration.configure({ document: client.doc, field: cardField(id) }),
        Placeholder.configure({
          placeholder: ({ node, pos }) =>
            pos === 0 && node.type.name === 'heading'
              ? t('card.titlePlaceholder')
              : t('card.bodyPlaceholder'),
          showOnlyCurrent: false,
        }),
      ],
      editable: !client.readOnly,
      editorProps: { attributes: { class: 'card-prose', spellcheck: 'false' } },
      onCreate: () => {
        dbg('editor create', id)
      },
      onDestroy: () => {
        dbg('editor destroy', id)
      },
    },
    [id, client.doc],
  )

  useEffect(() => {
    if (!editor) return
    const dom = editor.view.dom
    const onLink = (e: Event) => {
      openLink((e as CustomEvent<string>).detail)
    }
    dom.addEventListener(BOARD_LINK_EVENT, onLink)
    return () => {
      dom.removeEventListener(BOARD_LINK_EVENT, onLink)
    }
  }, [editor, openLink])

  useEffect(() => {
    if (!editor) return
    const text = initialText.get(id)
    if (text !== undefined) {
      initialText.delete(id)
      const [first = '', ...rest] = text.split('\n')
      editor.commands.setContent({
        type: 'doc',
        content: [
          {
            type: 'heading',
            attrs: { level: 1 },
            content: first ? [{ type: 'text', text: first }] : [],
          },
          ...rest.map((line) => ({
            type: 'paragraph',
            content: line ? [{ type: 'text', text: line }] : [],
          })),
        ],
      })
    }
  }, [editor, id, initialText])

  // Focus a freshly created card, but never take focus from anything else: if
  // the person has already moved on (clicked a title, opened a composer), stop.
  // The view can attach a frame or two after mount, and the editor can be
  // re-created right away (StrictMode), so retry briefly and let the flag
  // outlive this instance for a moment.
  useEffect(() => {
    if (!editor || !autofocus.has(id)) return
    let frames = 0
    let raf = 0
    const tick = () => {
      if (editor.isDestroyed) return
      const active = document.activeElement
      const dom = editor.view.dom
      if (dom.contains(active)) return
      if (active && active !== document.body) return
      if (dom.isConnected) {
        editor.commands.setTextSelection(editor.state.doc.content.size)
        editor.view.focus()
        if (dom.contains(document.activeElement)) return
      }
      if (frames++ < 30) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    const done = setTimeout(() => autofocus.delete(id), 600)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(done)
    }
  }, [editor, id, autofocus])

  return (
    <>
      <EditorContent editor={editor} className={`${NO_DRAG} cursor-text px-3.5 pb-3`} />
      {editor && !client.readOnly && (
        <>
          <SelectionBar editor={editor} cardId={id} />
          <SuggestList store={stores.mention} />
          <SuggestList store={stores.slash} />
          <MathEditor editor={editor} store={stores.math} />
          <BlockHandle editor={editor} cardId={id} />
        </>
      )}
    </>
  )
})

/** How many board messages point at this card; click to look at them together. */
function CommentCount({ id }: { id: string }) {
  const { actions } = useBoardCtx()
  const { t } = useI18n()
  const comments = useBoardDerived().comments.get(id) ?? []
  if (!comments.length) return null
  return (
    <button
      type="button"
      title={t('anchor.showComments', { n: comments.length })}
      aria-label={t('anchor.showComments', { n: comments.length })}
      onClick={(e) => {
        e.stopPropagation()
        actions.current?.showItems([id, ...comments])
      }}
      className={clsx(
        NO_DRAG,
        'ml-auto inline-flex items-center gap-1 rounded-md px-1.5 text-[11.5px] text-muted hover:bg-surface-2 hover:text-ink',
      )}
    >
      <MessageSquare size={12} />
      {comments.length}
    </button>
  )
}

/**
 * A card: author line (the drag handle) and its text. Auto height by default;
 * with `fixedHeight` the text is clipped with a fade and scrolls inside once
 * the card is selected.
 */
export function CardView({ item, selected }: { item: ItemOf<'card'>; selected: boolean }) {
  const fixed = item.data.fixedHeight ?? null
  return (
    <div
      data-card-id={item.id}
      className={clsx(
        'moot-node relative flex flex-col rounded-md border bg-surface shadow-sm transition-shadow',
        selected ? 'border-(--focus) shadow-md' : 'border-line hover:shadow-md',
        fixed !== null && 'overflow-hidden',
      )}
      style={{ width: item.w, ...(fixed !== null && { height: fixed }) }}
    >
      <div
        className={clsx(
          DRAG_HANDLE,
          'flex h-7 flex-none cursor-grab items-center px-3.5 pt-1 active:cursor-grabbing',
        )}
      >
        <Author actor={item.createdBy} quiet />
        <CommentCount id={item.id} />
      </div>
      <div
        className={clsx(
          'min-h-0 flex-1',
          fixed !== null && (selected ? clsx('overflow-y-auto', NO_WHEEL) : 'overflow-hidden'),
        )}
      >
        <CardEditor id={item.id} />
      </div>
      {fixed !== null && !selected && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 rounded-b-md bg-linear-to-b from-transparent to-surface" />
      )}
    </div>
  )
}
